import { decodeEntitiesForHtmlSanitizer, replaceAll } from './helpers';
import * as sanitizeHtml from 'sanitize-html';

export function initializeSummerNoteIgnoredInValidation() {
    jQuery.validator.setDefaults({ ignore: ".note-editor *" }); // inspired by https://stackoverflow.com/a/56033570/379279; summernote does not work well with jquery validation
}

export function attachSummernote(
    textAreas: JQuery,
    onChange: (sanitizedHtml: string) => void = ((sanitizedHtml: string) => { }),
) {
    textAreas.each(function() {
        const textArea = $(this);
        sanitizeTextAreaHtmlAndRemoveEmptyParagraphsAndBrTags(textArea);

        // @ts-ignore (due to linkTargetBlank)
        textArea.summernote({
            placeholder: textArea.attr("placeholder"),
            tabsize: 2,
            height: 200,
            codeviewFilter: true,
            codeviewIframeFilter: true,
            linkTargetBlank: false,
            toolbar: [
                ['view', ['codeview']],
                ['font', ['bold', 'italic', 'underline', 'clear']],
                ['para', ['ul', 'ol']],
                ['insert', ['link']],
                ['view', ['fullscreen']]
            ],
            callbacks: {
                onChange(html, $editable) {
                    let wasSanitized: boolean,
                        sanitizedHtml: string;
                    [wasSanitized, sanitizedHtml] = sanitize(html, true);
                    if (wasSanitized) {
                        $editable.html(sanitizedHtml);
                    }
                },
                onPaste(e) { 
                    // taken from https://stackoverflow.com/a/31019586/379279
                    // @ts-ignore
                    var bufferText = ((e.originalEvent || e).clipboardData || window.clipboardData).getData('Text');
                    e.preventDefault();
                    document.execCommand('insertText', false, bufferText);
                }
            }
        });
        textArea.on("summernote.change", (event, contents, $editable) => {
            var html = $editable.html();
            populateTextAreaHtmlWithoutEmptyParagraphsAndBrTags(textArea, html);
            onChange(html);
        });
    });
}

function sanitizeTextAreaHtmlAndRemoveEmptyParagraphsAndBrTags(textArea : JQuery<HTMLElement>) {
    const html = textArea.val() as string;
    let wasSanitized: boolean,
        sanitizedHtml: string;
    [wasSanitized, sanitizedHtml] = sanitize(html, true);
    populateTextAreaHtmlWithoutEmptyParagraphsAndBrTags(textArea, wasSanitized ? sanitizedHtml : html, false);
}

export function sanitize(
    html: string,
    isLoggingEnabled: boolean = false
    ) : [boolean, string] {
    const sanitizedHtml = sanitizeHtml(
        html.trim(),
        {
            allowedTags: ["p", "b", "i", "u", "a", "ul", "ol", "li", "br", "sub", "sup", "h1", "h2", "h3", "h4", "h5", "h6"], // keep this tag list in sync with SimpleHtmlValidator.cs
            allowedAttributes: {
                "a": ["href"],
                "h1": ["class"],
                "h2": ["class"],
                "h3": ["class"],
                "h4": ["class"],
                "h5": ["class"],
                "h6": ["class"],
            },
            allowedSchemes: ['http', 'https', 'smartguide', 'mailto', 'tel'],
            allowProtocolRelative: false,
            transformTags: {
                "strong": "b",
                "em": "i"
            }
    });

    const htmlToCompareWithSanitizedHtml = decodeEntitiesForHtmlSanitizer(
        replaceAll(html, "<br>", "<br />"));
    let wasSanitized = false;
    if (htmlToCompareWithSanitizedHtml !== sanitizedHtml) {
        wasSanitized = true;
        if (isLoggingEnabled) console.log(`html has been sanitized:\n\nOriginal: ${html}\n\nSanitized: ${sanitizedHtml}`);
    }
    return [wasSanitized, sanitizedHtml];
}

function populateTextAreaHtmlWithoutEmptyParagraphsAndBrTags(textArea: JQuery<HTMLElement>, html: string, shouldTriggerValidation: boolean = true) {
    html = removeEmptyParagraphsAndBrTags(html, true);
    html = wrapNonHtmlTextIntoParagraph(html);
    textArea.val(html);
    // @ts-ignore
    if (textArea[0].form && shouldTriggerValidation) { // dynamically reloaded textarea hanging outside the form -> ignore triggering validation for the textarea
        textArea.valid();
    }
}

export function wrapNonHtmlTextIntoParagraph(html: string) : string {
    if (!html) return html;

    var indexOfAngleBracket = html.indexOf("<");
    if (indexOfAngleBracket === -1) {
        return `<p>${html}</p>`;
    }
    var htmlAtTheStart = "";
    var remainingHtml = html;
    if (indexOfAngleBracket > 0) {
        var nonHtmlTextAtTheStart = html.substring(0, indexOfAngleBracket);
        htmlAtTheStart = `<p>${nonHtmlTextAtTheStart}</p>`;
        remainingHtml = html.substring(indexOfAngleBracket);
    }
    var indexOfLastClosingAngleBracket = remainingHtml.lastIndexOf(">");
    var htmlAtTheEnd = "";
    if (indexOfLastClosingAngleBracket !== -1 && indexOfLastClosingAngleBracket < remainingHtml.length - 1) {
        var nonHtmlTextAtTheEnd = remainingHtml.substring(indexOfLastClosingAngleBracket + 1);
        htmlAtTheEnd = `<p>${nonHtmlTextAtTheEnd}</p>`;
        remainingHtml = remainingHtml.substring(0, indexOfLastClosingAngleBracket + 1);  
    }
    return `${htmlAtTheStart}${remainingHtml}${htmlAtTheEnd}`;
}

// the regex pattern below is also in C# EmptyParagraphsAndBrsFixer class
export function removeEmptyParagraphsAndBrTags(html: string, isLoggingEnabled: boolean = false) : string {
    const fixedHtml = html
        .replace(/(((<p>)(<br\s?\/?>|\s)*(<\/p>))|(<br\s?\/?>|\s))*$/gmi, "")
        .replace(/^(((<p>)(<br\s?\/?>|\s)*(<\/p>))|(<br\s?\/?>|\s))*/gmi, "")
        .replace(/((<p>)(\s)*(<\/p>))*/gmi, "")
        .replace(/((<br\s?\/?>|\s)*(<\/p>))$/gmi, "</p>");
    if (isLoggingEnabled && fixedHtml !== html) {
        console.log(`html empty paragraphs and <br> tags has been removed:\n\nOriginal: ${html}\n\nFixed: ${fixedHtml}`);
    }
    return fixedHtml;
}

// taken from https://github.com/summernote/summernote/issues/3088#issuecomment-461618517
export function fixChromeWrappingTextInSpanWhenDeletingLineBreak() { 
    $(document).on("DOMNodeInserted", '.note-editable', e => {
        if (e.target.tagName === "SPAN") {
            $(e.target).replaceWith($(e.target).contents());
        }
    });
}
